import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Upload, CheckCircle2, Copy, ArrowRight, Info } from "lucide-react";
import { useLang, DIRECTION_LABEL } from "@/i18n";
import { api, apiError } from "@/lib/api";
import { fmtGBP, fmtIDR, fmtDate, gbpToIdr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CapacityMeter } from "@/components/CapacityMeter";
import { TermsContent, TermsModal } from "@/components/TermsModal";

const emptyItem = () => ({ name: "", quantity: 1, price_value: "", receipt_file_path: null, receipt_filename: null });

export default function BookingPage() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState(params.get("trip") || "");
  const [form, setForm] = useState({
    customer_name: "", phone: "", whatsapp: "", estimated_weight_kg: "", is_bulky: false,
    delivery_option: "pickup", delivery_payer: "customer", recipient_address: "",
  });
  const [items, setItems] = useState([emptyItem()]);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/trips").then(({ data }) => setTrips(data)); }, []);
  const trip = useMemo(() => trips.find((x) => x.id === tripId), [trips, tripId]);

  const estFee = useMemo(() => {
    if (!trip || !form.estimated_weight_kg) return null;
    const w = Number(form.estimated_weight_kg);
    if (form.is_bulky) {
      if (trip.bulky_pricing_type === "flat") return trip.bulky_fee_gbp;
      if (trip.bulky_pricing_type === "per_kg") return w * trip.bulky_fee_gbp;
      return null; // quote
    }
    return w * trip.standard_fee_gbp_per_kg;
  }, [trip, form.estimated_weight_kg, form.is_bulky]);

  const remaining = trip ? trip.remaining_capacity_kg : 0;
  const bookable = !!trip && trip.order_open !== false && trip.active !== false && remaining > 0;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const uploadReceipt = async (i, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItem(i, "receipt_file_path", data.path);
      setItem(i, "receipt_filename", data.filename);
      toast.success(t("uploaded"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const validate = () => {
    const e = {};
    if (!tripId) e.trip = t("err_trip");
    if (!form.customer_name.trim()) e.customer_name = t("err_name");
    if (!form.phone.trim() || form.phone.trim().length < 6) e.phone = t("err_phone");
    if (!form.whatsapp.trim()) e.whatsapp = t("err_required");
    if (!form.estimated_weight_kg || Number(form.estimated_weight_kg) <= 0) e.weight = t("err_weight");
    if (!items.some((it) => it.name.trim())) e.items = t("err_item");
    else if (items.some((it) => it.name.trim() && (!it.quantity || Number(it.quantity) < 1 || it.price_value === "" || it.price_value == null || Number(it.price_value) < 0))) e.items = t("err_item_fields");
    if (form.delivery_option === "delivery" && !form.recipient_address.trim()) e.recipient = t("err_recipient");
    if (!terms) e.terms = t("err_terms");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) { toast.error(t("err_required")); return; }
    setSubmitting(true);
    try {
      const payload = {
        trip_id: tripId,
        customer_name: form.customer_name, phone: form.phone, whatsapp: form.whatsapp,
        estimated_weight_kg: Number(form.estimated_weight_kg), is_bulky: form.is_bulky,
        items: items.filter((it) => it.name.trim()).map((it) => ({
          name: it.name, quantity: Number(it.quantity) || 1, price_value: Number(it.price_value) || 0,
          receipt_file_path: it.receipt_file_path, receipt_filename: it.receipt_filename,
        })),
        delivery_option: form.delivery_option,
        delivery_payer: form.delivery_option === "delivery" ? form.delivery_payer : null,
        recipient_address: form.delivery_option === "delivery" ? form.recipient_address : null,
        terms_accepted: terms,
      };
      const { data } = await api.post("/bookings", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card data-testid="booking-success" className="p-8 text-center space-y-5 animate-fade-up">
          <CheckCircle2 className="h-16 w-16 text-primary mx-auto" />
          <h1 className="text-2xl font-bold font-display">{t("booking_success")}</h1>
          <p className="text-muted-foreground">{t("save_ref")}</p>
          <p className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-testid="waiting-notice">{t("waiting_notice")}</p>
          <div className="flex items-center justify-center gap-2 bg-secondary rounded-xl py-4">
            <span data-testid="result-reference" className="font-mono text-2xl font-bold tracking-wider">{result.reference}</span>
            <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(result.reference); toast.success("Copied"); }}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          {result.estimated_fee_gbp != null && (
            <p className="text-sm">{t("est_fee")}: <span className="font-semibold">{fmtGBP(result.estimated_fee_gbp)}</span></p>
          )}
          <div className="flex gap-3 justify-center pt-2">
            <Button variant="outline" onClick={() => { setResult(null); setForm({ customer_name: "", phone: "", whatsapp: "", estimated_weight_kg: "", is_bulky: false, delivery_option: "pickup", delivery_payer: "customer", recipient_address: "" }); setItems([emptyItem()]); setTerms(false); }}>
              {t("book_another")}
            </Button>
            <Button data-testid="goto-track-btn" onClick={() => navigate(`/track?ref=${result.reference}`)}>{t("track_now")}</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight font-display">{t("booking_title")}</h1>
        <p className="text-muted-foreground mt-1">{t("booking_sub")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Trip select */}
          <Card className="p-6 space-y-3">
            <Label className="font-semibold">{t("select_trip")}</Label>
            <Select value={tripId} onValueChange={setTripId}>
              <SelectTrigger data-testid="trip-select"><SelectValue placeholder={t("select_trip")} /></SelectTrigger>
              <SelectContent>
                {trips.map((tr) => (
                  <SelectItem key={tr.id} value={tr.id} data-testid={`trip-option-${tr.id}`}>{tr.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.trip && <p className="text-sm text-destructive">{errors.trip}</p>}
          </Card>

          {/* Customer */}
          <Card className="p-6 space-y-4">
            <h2 className="font-display font-semibold text-lg">{t("customer_info")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("full_name")} error={errors.customer_name}>
                <Input data-testid="input-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
              </Field>
              <Field label={t("phone")} error={errors.phone}>
                <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0812... / +44..." />
              </Field>
              <Field label={t("whatsapp")} error={errors.whatsapp}>
                <Input data-testid="input-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
              </Field>
              <Field label={t("total_weight")} error={errors.weight}>
                <Input data-testid="input-weight" type="number" min="0" step="0.1" value={form.estimated_weight_kg}
                  onChange={(e) => set("estimated_weight_kg", e.target.value)} />
              </Field>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch data-testid="switch-bulky" checked={form.is_bulky} onCheckedChange={(v) => set("is_bulky", v)} />
              <Label>{t("is_bulky")}</Label>
            </div>
          </Card>

          {/* Items */}
          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-lg">{t("items")}</h2>
            </div>
            <p className="text-sm text-muted-foreground flex gap-2"><Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" /> {t("item_hint")}</p>
            {errors.items && <p className="text-sm text-destructive">{errors.items}</p>}
            <div className="space-y-4">
              {items.map((it, i) => (
                <div key={i} data-testid={`item-row-${i}`} className="rounded-xl border border-border p-4 space-y-3 relative">
                  <div className="grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-6">
                      <Label className="text-xs">{t("item_name")}</Label>
                      <Input data-testid={`item-name-${i}`} value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">{t("qty")}</Label>
                      <Input data-testid={`item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                    </div>
                    <div className="sm:col-span-4">
                      <Label className="text-xs">{t("price_value")} (£)</Label>
                      <Input data-testid={`item-price-${i}`} type="number" min="0" step="0.01" value={it.price_value} onChange={(e) => setItem(i, "price_value", e.target.value)} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline" data-testid={`item-upload-label-${i}`}>
                      <Upload className="h-4 w-4" />
                      {it.receipt_filename ? `${t("uploaded")}: ${it.receipt_filename}` : t("receipt")}
                      <input type="file" className="hidden" accept="image/*,.pdf" data-testid={`item-upload-${i}`}
                        onChange={(e) => e.target.files[0] && uploadReceipt(i, e.target.files[0])} />
                    </label>
                    {items.length > 1 && (
                      <Button variant="ghost" size="sm" data-testid={`item-remove-${i}`} onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}>
                        <Trash2 className="h-4 w-4 mr-1" /> {t("remove")}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" data-testid="add-item-btn" onClick={() => setItems((p) => [...p, emptyItem()])}>
              <Plus className="h-4 w-4 mr-1" /> {t("add_item")}
            </Button>
          </Card>

          {/* Delivery */}
          <Card className="p-6 space-y-4">
            <h2 className="font-display font-semibold text-lg">{t("delivery_method")}</h2>
            <RadioGroup value={form.delivery_option} onValueChange={(v) => set("delivery_option", v)} className="space-y-2">
              <label className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer" data-testid="delivery-option-pickup">
                <RadioGroupItem value="pickup" /> <span className="text-sm">{t("delivery_pickup")}</span>
              </label>
              <label className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer" data-testid="delivery-option-deliver">
                <RadioGroupItem value="delivery" /> <span className="text-sm">{t("delivery_deliver")}</span>
              </label>
            </RadioGroup>
            {form.delivery_option === "delivery" && (
              <div className="space-y-4 pt-1">
                <Field label={t("recipient_address")} error={errors.recipient}>
                  <Textarea data-testid="input-recipient" value={form.recipient_address} onChange={(e) => set("recipient_address", e.target.value)} />
                </Field>
                <div>
                  <Label className="text-sm">{t("who_pays")}</Label>
                  <RadioGroup value={form.delivery_payer} onValueChange={(v) => set("delivery_payer", v)} className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="payer-customer"><RadioGroupItem value="customer" /> <span className="text-sm">{t("payer_customer")}</span></label>
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="payer-recipient"><RadioGroupItem value="recipient" /> <span className="text-sm">{t("payer_recipient")}</span></label>
                  </RadioGroup>
                </div>
              </div>
            )}
          </Card>

          {/* Terms */}
          <Card className="p-6 space-y-4">
            <h2 className="font-display font-semibold text-lg">{t("terms_title")}</h2>
            <div className="max-h-40 overflow-y-auto rounded-lg bg-secondary p-4"><TermsContent /></div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="terms-checkbox" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5" />
              <span className="text-sm">{t("terms_accept")}</span>
            </label>
            {errors.terms && <p className="text-sm text-destructive">{errors.terms}</p>}
          </Card>
        </div>

        {/* Summary sidebar */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-20 space-y-4">
            <Card className="p-6 space-y-4">
              <h2 className="font-display font-semibold text-lg">{t("summary")}</h2>
              {trip ? (
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold">{trip.title}</p>
                    <p className="text-primary font-medium">{DIRECTION_LABEL[trip.direction]}</p>
                  </div>
                  <div className="text-muted-foreground">
                    <p>{t("deadline")}: <span className="text-foreground font-medium">{fmtDate(trip.receive_deadline, lang)}</span> ({trip.deadline_timezone === "birmingham" ? "BHX" : "WIB"})</p>
                  </div>
                  <CapacityMeter capacity={trip.capacity_kg} used={trip.used_capacity_kg} remaining={trip.remaining_capacity_kg} />
                  <div className="border-t border-border pt-3">
                    <p className="text-muted-foreground text-xs">{t("est_fee")}</p>
                    <p data-testid="estimated-fee" className="text-2xl font-bold font-display text-primary">
                      {estFee != null ? fmtGBP(estFee) : "—"}
                    </p>
                    {estFee != null && (
                      <p className="text-xs text-muted-foreground font-mono">≈ {fmtIDR(gbpToIdr(estFee, trip.exchange_rate_gbp_to_idr))}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">{t("est_fee_hint")}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t("exchange_rate_used")}: £1 = {fmtIDR(trip.exchange_rate_gbp_to_idr)}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("err_trip")}</p>
              )}
              {trip && !bookable && (
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-testid="not-bookable-note">
                  {remaining <= 0 ? t("trip_full") : t("orders_closed")}
                </p>
              )}
              <Button className="w-full" size="lg" data-testid="submit-booking-btn" disabled={submitting || !bookable} onClick={submit}>
                {submitting ? t("loading") : t("submit_booking")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <TermsModal trigger={<Button variant="ghost" size="sm" className="w-full" data-testid="view-terms-btn">{t("view_terms")}</Button>} />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}</Label>
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);
